import type { ReactElement, ReactNode } from 'react'

export interface FilterDefinitionActiveState {
  badge?: ReactNode
  count: number
}

export interface FilterDefinition<
  StateParams,
  CommandGroupParams,
  SelectedParams,
  SelectorParams,
  OperatorParams,
> {
  active: (params: StateParams) => FilterDefinitionActiveState
  clear?: (params: StateParams) => void
  commandGroup: (params: CommandGroupParams) => ReactElement | null
  group?: string
  info?: string
  keywords?: Array<string>
  label: string
  operator?: (params: OperatorParams) => ReactNode | null
  property: string
  selected: (params: SelectedParams) => ReactNode | null
  selector: (params: SelectorParams) => ReactNode
}

export function createFilterDefinitionActiveStateFromBadge(
  badge: ReactNode | null | undefined,
) {
  if (typeof badge === 'number') {
    return badge > 0
      ? {
          badge,
          count: badge,
        }
      : {
          count: 0,
        }
  }

  if (badge === null || badge === undefined || typeof badge === 'boolean') {
    return {
      count: 0,
    }
  }

  return {
    badge,
    count: 1,
  }
}

export interface FilterDefinitionGroup<Definition> {
  filterDefinitions: Array<Definition>
  title: string | null
}

export interface GroupFilterDefinitionsParams<Definition> {
  filterDefinitions: Array<Definition & { group?: string }>
}

export function groupFilterDefinitions<Definition>({
  filterDefinitions,
}: GroupFilterDefinitionsParams<Definition>) {
  const groups: Array<FilterDefinitionGroup<Definition & { group?: string }>> =
    []
  for (const definition of filterDefinitions) {
    const groupTitle = definition.group ?? null
    const existingGroup = groups.find((group) => {
      return group.title === groupTitle
    })
    if (existingGroup) {
      existingGroup.filterDefinitions.push(definition)
    } else {
      groups.push({
        filterDefinitions: [definition],
        title: groupTitle,
      })
    }
  }

  return groups
}

export interface CountActiveFilterDefinitionsParams<Definition, StateParams> {
  filterDefinitions: Array<
    Definition & {
      active: (params: StateParams) => FilterDefinitionActiveState
    }
  >
  stateParams: StateParams
}

export function countActiveFilterDefinitions<Definition, StateParams>({
  filterDefinitions,
  stateParams,
}: CountActiveFilterDefinitionsParams<Definition, StateParams>) {
  return filterDefinitions.reduce((count, definition) => {
    return count + definition.active(stateParams).count
  }, 0)
}

export interface SelectedFilterDefinitionItem {
  id: string
  node: ReactNode
}

export interface GetSelectedFilterDefinitionItemsParams<
  Definition,
  StateParams,
  SelectedParams,
> {
  filterDefinitions: Array<
    Definition & {
      active: (params: StateParams) => FilterDefinitionActiveState
      label: string
      property: string
      selected: (params: SelectedParams) => ReactNode | null
    }
  >
  getSelectedParams: (
    definition: Definition & { label: string; property: string },
  ) => SelectedParams
  stateParams: StateParams
}

export function getSelectedFilterDefinitionItems<
  Definition,
  StateParams,
  SelectedParams,
>({
  filterDefinitions,
  getSelectedParams,
  stateParams,
}: GetSelectedFilterDefinitionItemsParams<
  Definition,
  StateParams,
  SelectedParams
>) {
  return filterDefinitions
    .map((definition): SelectedFilterDefinitionItem | null => {
      if (definition.active(stateParams).count === 0) {
        return null
      }

      const selectedParams = getSelectedParams(definition)
      const node = definition.selected(selectedParams)
      return node !== null && node !== undefined && typeof node !== 'boolean'
        ? {
            id: definition.property,
            node,
          }
        : null
    })
    .filter(Boolean)
}
